import React, { useState, useEffect, useRef } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router';
import faqData from '../../assets/json/faqData.json';
import rafFaqData from '../../assets/json/rafFaqData.json';
import useIsMobile from '../../hooks/useIsMobile';

interface FaqItem {
  q: string;
  a: string;
}

interface FaqSection {
  section: string;
  items: FaqItem[];
}

interface RafFaqItem {
  id: number;
  question: string;
  answer: string;
}

const COLLAPSE_DURATION_MS = 300;

const CollapsiblePanel: React.FC<{
  isOpen: boolean;
  children: React.ReactNode;
  className?: string;
  innerClassName?: string;
}> = ({ isOpen, children, className = '', innerClassName = '' }) => (
  <div
    className={`grid transition-[grid-template-rows] duration-300 ease-in-out motion-reduce:transition-none ${
      isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
    } ${className}`}
    aria-hidden={!isOpen}
  >
    <div className='overflow-hidden min-h-0'>
      <div
        className={`transition-opacity duration-300 ease-in-out motion-reduce:transition-none ${
          isOpen ? 'opacity-100' : 'opacity-0'
        } ${innerClassName}`}
      >
        {children}
      </div>
    </div>
  </div>
);

const ToggleIcon: React.FC<{ isOpen: boolean }> = ({ isOpen }) => (
  <span
    className='flex-shrink-0 w-[26px] h-[26px] rounded-full border-[1.5px] border-[#333333] flex items-center justify-center bg-transparent text-[#333333]'
    aria-hidden='true'
  >
    <svg
      width='12'
      height='12'
      viewBox='0 0 12 12'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
    >
      <line
        x1='1'
        y1='6'
        x2='11'
        y2='6'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
      />
      <line
        x1='6'
        y1='1'
        x2='6'
        y2='11'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        className={`origin-center transition-opacity duration-300 ease-in-out motion-reduce:transition-none ${
          isOpen ? 'opacity-0' : 'opacity-100'
        }`}
      />
    </svg>
  </span>
);

const FaqAnswer: React.FC<{ html: string }> = ({ html }) => (
  <div
    className='faq-answer text-base text-[#666666] leading-[1.7] [&_ul]:my-2 [&_ul]:ml-5 [&_ol]:my-2 [&_ol]:ml-5 [&_li]:mb-1 [&_a]:text-primary [&_strong]:font-semibold'
    dangerouslySetInnerHTML={{ __html: html }}
  />
);

const FaqQuestionItem: React.FC<{
  item: FaqItem;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ item, isOpen, onToggle }) => (
  <div className='border-b border-[#e0e0e0] last:border-b-0'>
    <button
      type='button'
      className='w-full flex items-center justify-between gap-3 py-4 px-3 mx:px-5 text-left cursor-pointer select-none'
      onClick={onToggle}
      aria-expanded={isOpen}
    >
      <p className='text-lg text-[#333333] flex-1'>{item.q}</p>
      <ToggleIcon isOpen={isOpen} />
    </button>
    <CollapsiblePanel isOpen={isOpen} innerClassName='pb-4 pl-5'>
      <FaqAnswer html={item.a} />
    </CollapsiblePanel>
  </div>
);

const FaqSectionBlock: React.FC<{
  section: FaqSection;
  isOpen: boolean;
  onToggleSection: () => void;
  openQuestionKey: string | null;
  onToggleQuestion: (key: string) => void;
  sectionIndex: number;
  isFirst: boolean;
  sectionRef?: React.Ref<HTMLDivElement>;
}> = ({
  section,
  isOpen,
  onToggleSection,
  openQuestionKey,
  onToggleQuestion,
  sectionIndex,
  isFirst,
  sectionRef,
}) => (
  <div
    ref={sectionRef}
    className={`border-b border-[#e0e0e0] scroll-mt-24 ${isFirst ? 'border-t' : ''}`}
  >
    <button
      type='button'
      className='w-full flex items-center justify-between gap-3 py-4 md:py-9 text-left cursor-pointer select-none'
      onClick={onToggleSection}
      aria-expanded={isOpen}
    >
      <h2 className='text-lg md:text-xl font-semibold text-primary flex-1'>
        {section.section}
      </h2>
      <ToggleIcon isOpen={isOpen} />
    </button>
    <CollapsiblePanel isOpen={isOpen}>
      <div>
        {section.items.map((item, itemIndex) => {
          const key = `${sectionIndex}-${itemIndex}`;
          return (
            <FaqQuestionItem
              key={key}
              item={item}
              isOpen={openQuestionKey === key}
              onToggle={() => onToggleQuestion(key)}
            />
          );
        })}
      </div>
    </CollapsiblePanel>
  </div>
);

const RafFaqItemRow: React.FC<{
  item: RafFaqItem;
  isOpen: boolean;
  onToggle: () => void;
  isFirst: boolean;
  itemRef?: React.Ref<HTMLDivElement>;
}> = ({ item, isOpen, onToggle, isFirst, itemRef }) => (
  <div
    ref={itemRef}
    className={`bg-white border-b border-[#e0e0e0] scroll-mt-24 ${isFirst ? 'border-t' : ''}`}
  >
    <button
      type='button'
      className='w-full flex items-center justify-between gap-3 py-[18px] text-left cursor-pointer select-none'
      onClick={onToggle}
      aria-expanded={isOpen}
    >
      <p className='text-sm text-[#333333] flex-1 pr-4'>{item.question}</p>
      <ToggleIcon isOpen={isOpen} />
    </button>
    <CollapsiblePanel isOpen={isOpen} innerClassName='pb-4 pl-5'>
      <p className='text-sm text-[#666666] leading-[1.7]'>{item.answer}</p>
    </CollapsiblePanel>
  </div>
);

const QuestionsDropdown = () => {
  const { pathname } = useLocation();
  const { isMobile } = useIsMobile();
  const isFaqPage =
    pathname.includes('/business-faq') || pathname.includes('/raf-faq');
  const isRafFaq = pathname.includes('/raf-faq');
  const isBusinessFaq = !isRafFaq;

  const [expand, setExpand] = useState(false);
  const [openSectionIndex, setOpenSectionIndex] = useState<number | null>(null);
  const [openQuestionKey, setOpenQuestionKey] = useState<string | null>(null);
  const [openRafIndex, setOpenRafIndex] = useState<number | null>(null);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const rafItemRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (isFaqPage) {
      setExpand(true);
    }
  }, [isFaqPage]);

  useEffect(() => {
    if (openSectionIndex === null) return;

    const timer = setTimeout(() => {
      sectionRefs.current[openSectionIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, COLLAPSE_DURATION_MS);

    return () => clearTimeout(timer);
  }, [openSectionIndex]);

  useEffect(() => {
    if (openRafIndex === null) return;

    const timer = setTimeout(() => {
      rafItemRefs.current[openRafIndex]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, COLLAPSE_DURATION_MS);

    return () => clearTimeout(timer);
  }, [openRafIndex]);

  const sections = faqData as FaqSection[];
  const previewSections = sections.slice(0, 3);
  const displaySections = expand ? sections : previewSections;

  const handleToggleSection = (index: number) => {
    setOpenSectionIndex(prev => {
      if (prev === index) {
        setOpenQuestionKey(null);
        return null;
      }
      setOpenQuestionKey(null);
      return index;
    });
  };

  const handleToggleQuestion = (key: string, sectionIndex: number) => {
    if (openSectionIndex !== sectionIndex) {
      setOpenSectionIndex(sectionIndex);
      setOpenQuestionKey(key);
      return;
    }
    setOpenQuestionKey(prev => (prev === key ? null : key));
  };

  const handleToggleRaf = (index: number) => {
    setOpenRafIndex(prev => (prev === index ? null : index));
  };

  const rafItems = expand
    ? rafFaqData
    : rafFaqData.filter(item => [1, 2, 3].includes(item.id));

  return (
    <div
      className={`px-4 md:px-auto md:w-full ${
        isFaqPage ? 'pb-12' : 'bg-primaryBg-50'
      }`}
    >
      {!isMobile && (
        <h2 className='text-2xl md:text-[38px] pt-10 pb-4 md:pt-20 md:pb-8 font-bold text-primary text-center'>
          Questions?
        </h2>
      )}
      <p
        className={`max-w-3xl md:mx-auto mb-8 md:mb-16 text-black leading-7 ${
          isMobile ? 'text-left mt-8 text-[18px]' : 'text-center text-[20px]'
        }`}
      >
        If you have questions, our FAQ section is here to help. Browse through
        to find detailed answers and helpful information on a variety of
        topics.
      </p>

      <div className='mt-6 md:mx-auto max-w-[820px]'>
        {isRafFaq
          ? rafItems.map((item, index) => (
              <RafFaqItemRow
                key={item.id}
                item={item}
                isOpen={openRafIndex === index}
                onToggle={() => handleToggleRaf(index)}
                isFirst={index === 0}
                itemRef={el => {
                  rafItemRefs.current[index] = el;
                }}
              />
            ))
          : displaySections.map((section, sectionIndex) => (
              <FaqSectionBlock
                key={section.section}
                section={section}
                sectionIndex={sectionIndex}
                isOpen={openSectionIndex === sectionIndex}
                onToggleSection={() => handleToggleSection(sectionIndex)}
                openQuestionKey={openQuestionKey}
                onToggleQuestion={key =>
                  handleToggleQuestion(key, sectionIndex)
                }
                isFirst={sectionIndex === 0}
                sectionRef={el => {
                  sectionRefs.current[sectionIndex] = el;
                }}
              />
            ))}
      </div>

      {!expand && (
        <Link to={isRafFaq ? '/raf-faq' : '/business-faq'}>
          <div className='flex flex-row justify-end pt-8 pb-20 gap-2 items-center max-w-[820px] mx-auto'>
            <p className='text-primary font-semibold hover:underline'>
              Read more{' '}
            </p>
            <FaExternalLinkAlt className='text-primary font-semibold' />
          </div>
        </Link>
      )}

      {expand && isBusinessFaq && (
        <p className='text-center text-[13px] text-[#555f7a] pt-6 pb-2 max-w-[820px] mx-auto'>
          Can't find what you're looking for? Email{' '}
          <a
            href='mailto:support@saversapp.com'
            className='text-primary hover:underline'
          >
            support@saversapp.com
          </a>{' '}
          and we'll help directly.
        </p>
      )}
    </div>
  );
};

export default QuestionsDropdown;
